// src/components/widget/players/MonitorVideoPlayer.tsx
import React, { useEffect, useRef, useState } from 'react';
import Icon            from '../images/Icon';
import AnimatedWaitingIcon from '../animations/animatedWaitingIcon';
import styles          from './SimpleHlsPlayer.module.scss';

type PlayerState =
  | 'off'          // arrêt  — sleepingTV / logo
  | 'starting'     // arrêt → marche  — AnimatedIcon seul
  | 'playing'      // signal présent  — vidéo seule
  | 'signal-lost'  // signal sauté pendant marche — vidéo freeze + AnimatedIcon
  | 'stopping';    // marche → arrêt, signal encore présent — vidéo + AnimatedIcon

interface MonitorVideoPlayerProps {
  mediaStream : MediaStream | null;
  active      : boolean;               // ← NOUVEAU : piloté par le parent
  className?  : string;
  onClick?    : () => void;
  logo?       : string;
}

export const MonitorVideoPlayer: React.FC<MonitorVideoPlayerProps> = ({
  mediaStream,
  active,
  className = '',
  onClick,
  logo,
}) => {
  const videoRef      = useRef<HTMLVideoElement>(null);
  const lastStreamRef = useRef<MediaStream | null>(null);
  const containerRef  = useRef<HTMLDivElement>(null);

  // ── Taille du conteneur ───────────────────────────────────────────────────
  // L'icône d'attente était figée à 200×200 : dans les moniteurs étroits (celui du
  // flux final de la régie fait ~1/4 de la largeur du panneau, contre ~520px sur le
  // dashboard), la pile icône + libellé dépassait la hauteur disponible et se faisait
  // rogner par l'`overflow: hidden` du parent — anneau coupé, libellé rejeté hors cadre.
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      const w = Math.round(width);
      const h = Math.round(height);
      setBox(prev => (prev && prev.w === w && prev.h === h) ? prev : { w, h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Machine à états ──────────────────────────────────────────────────────
  const [playerState, setPlayerState] = useState<PlayerState>('off');

  // Les écouteurs vidéo ci-dessous sont posés une seule fois : ils lisent `active` par ref.
  const activeRef = useRef(active);
  activeRef.current = active;

  // Transitions pilotées par `active`
  useEffect(() => {
    if (active) {
      // off/stopping → starting (on attend le signal). On ne repasse PAS en 'starting'
      // depuis 'playing'/'signal-lost' : une réaffirmation de `active` alors que le flux
      // est déjà là masquerait la vidéo derrière l'icône d'attente.
      setPlayerState(prev =>
        prev === 'off' || prev === 'stopping' ? 'starting' : prev
      );
    } else {
      // marche → stopping si on avait un signal, sinon retour off direct
      setPlayerState(prev =>
        prev === 'playing' || prev === 'signal-lost' ? 'stopping' : 'off'
      );
    }
  }, [active]);

  // ── Attachement du stream ─────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (lastStreamRef.current === mediaStream && mediaStream !== null) return;
    lastStreamRef.current = mediaStream;

    if (!mediaStream) {
      video.srcObject = null;
      // Le signal vient de disparaître
      setPlayerState(prev => {
        if (prev === 'playing' || prev === 'signal-lost') return 'signal-lost';
        if (prev === 'stopping')                          return 'off';
        return 'off';
      });
      return;
    }

    video.srcObject = mediaStream;
    video.play().catch((err) => {
      if (err?.name !== 'AbortError') {
        console.warn('[MonitorVideoPlayer] autoplay blocked', err);
      }
    });
  }, [mediaStream]);

  // ── Filet : arrêt demandé et plus aucun flux → écran d'arrêt ───────────────────
  // 'signal-lost' et 'stopping' affichent un spinner en attendant que quelque chose
  // change. Quand `active` est faux et qu'il n'y a plus de flux, rien ne changera plus :
  // les transitions ci-dessus ne rejouent que sur un changement de `active`, déjà passé.
  // Sans ce filet, une chaîne arrêtée en pleine activation restait sur un spinner nu,
  // devant un écran noir, jusqu'au rechargement de la page.
  useEffect(() => {
    if (!active && !mediaStream) setPlayerState('off');
  }, [active, mediaStream]);

  // ── Event listeners vidéo ─────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlaying = () => {
      // Signal arrivé — on passe en playing quelle que soit l'origine. Sauf si la chaîne
      // n'est plus demandée (`active` faux) : le moniteur reste ouvert pendant la
      // désactivation et le flux, encore servi quelques secondes, peut arriver APRÈS l'arrêt
      // demandé. Ce n'est pas un signal à afficher comme tel mais un arrêt en cours.
      setPlayerState(prev =>
        prev === 'stopping'  ? 'stopping'  // on garde stopping : on attend que le signal parte
        : !activeRef.current ? 'stopping'
        : 'playing'
      );
    };

    const onStalled = () => {
      console.warn('[MonitorVideoPlayer] stalled — tentative de reprise');
      setPlayerState(prev =>
        prev === 'playing'  ? 'signal-lost' :
        prev === 'stopping' ? 'stopping'    : prev
      );
      video.play().catch(() => {});
    };

    const onError   = () => setPlayerState(prev =>
      prev === 'stopping' ? 'off' : 'signal-lost'
    );

    const onEmptied = () => setPlayerState(prev => {
      if (prev === 'stopping') return 'off';
      if (prev === 'playing')  return 'signal-lost';
      return prev;
    });

    video.addEventListener('playing', onPlaying);
    video.addEventListener('stalled', onStalled);
    video.addEventListener('error',   onError);
    video.addEventListener('emptied', onEmptied);

    return () => {
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('error',   onError);
      video.removeEventListener('emptied', onEmptied);
    };
  }, []);

  // ── Cleanup démontage ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => { videoRef.current?.pause(); };
  }, []);

  // ── Dérivation du rendu ───────────────────────────────────────────────────
  const showSleeping = playerState === 'off';
  const showVideo    = playerState === 'playing'
                    || playerState === 'signal-lost'
                    || playerState === 'stopping';
  const showSpinner  = playerState === 'starting'
                    || playerState === 'signal-lost'
                    || playerState === 'stopping';

  // ── Dimensionnement de l'overlay d'attente ────────────────────────────────
  // Sous ~90px de haut il n'y a plus la place d'un libellé lisible : on garde
  // l'icône seule plutôt que d'afficher un texte tronqué.
  const SPINNER_GAP = 10;
  const LABEL_H     = 20;
  const hasLabel    = (playerState === 'starting' || playerState === 'stopping')
                   && (box === null || box.h >= 90);
  const reserved    = hasLabel ? SPINNER_GAP + LABEL_H : 0;
  // Pas encore mesuré : on garde les 200px historiques (taille des grands moniteurs).
  const iconSize    = box === null
    ? 200
    : Math.max(32, Math.min(200, box.w * 0.6, box.h - reserved - 8));
  const labelSize   = box === null ? 14 : Math.max(10, Math.min(14, box.w * 0.035));

  return (
    <div ref={containerRef} className={`${styles.playerContainer} ${className}`} onClick={onClick}>

      {/* ── Écran arrêt ── */}
{showSleeping && (
  <div className={styles.errorContainer}>
{logo
  ? (
    <img
      src={logo}
      alt=""
      // Proportions respectées ET jamais rognée : le logo se mesure sur le viewer, pas sur une
      // taille fixe. Avec 200px de plafond, un logo carré dépassait la hauteur d'un petit
      // viewer (16/9, ~150px de haut) et se faisait couper par son `overflow: hidden`.
      style={{
        maxWidth  : '80%',
        maxHeight : '80%',
        width     : 'auto',
        height    : 'auto',
        objectFit : 'contain',
      }}
    />
  )
  : <Icon name="sleepingTV" width={100} height={100} color="white" hoverable={false} clickable={false} />
}
  </div>
)}


      {/* ── Vidéo (freeze si signal perdu) ── */}
      <video
        ref        = {videoRef}
        className  = {`${styles.video} ${!showVideo ? styles.hidden : ''}`}
        autoPlay
        playsInline
        muted
        aria-label = "Lecture du flux vidéo retour"
        aria-live  = "polite"
      />

      {/* ── Spinner superposé ── */}
      {showSpinner && (
        <div className={styles.spinnerOverlay} style={{ gap: hasLabel ? SPINNER_GAP : 0 }}>
          <AnimatedWaitingIcon width={iconSize} height={iconSize} />
          {hasLabel && (
            <div className={styles.spinnerLabel} style={{ fontSize: labelSize }}>
              {playerState === 'starting' ? 'Démarrage de la chaîne' : 'Arrêt de la chaîne'}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default MonitorVideoPlayer;